"use client";
// Tenant colours are chosen for a light background, and in light mode they're
// used as-is (contrast there is the tenant's responsibility). Dark mode is the
// one place readability is checked: `brandText` / `brand2Text` are computed
// from the tenant colours — lightened until readable on the dark paper — and
// used as the text colour. The raw `brand` / `brand2` tokens stay untouched.
import { alpha } from "@mui/system/colorManipulator";
import { common } from "@mui/material/colors";
import { error, info, neutral, success, warning } from "../colors";
import { getPrimary } from "../utils";
import { createBrandColor, readableOn } from "@theme/shared/create-brand-color";

export const createPalette = (config) => {
  const { colorPreset, branding } = config;

  // Tenant branding. `clientPrimary` → palette.primary + the `brand` token,
  // `clientSecondary` → palette.secondary + the `brand2` token. Dark-mode defaults.
  const clientPrimary = createBrandColor(branding?.clientPrimary);
  const clientSecondary =
    createBrandColor(branding?.clientSecondary) ?? createBrandColor("#0D3D6E");

  return {
    action: {
      active: neutral[500],
      disabled: alpha(neutral[100], 0.38),
      disabledBackground: alpha(neutral[100], 0.12),
      focus: alpha(neutral[100], 0.16),
      hover: alpha(neutral[100], 0.04),
      selected: alpha(neutral[100], 0.12),
    },
    background: {
      default: "#1a222c",
      paper: "#24303f",
      tab: "#000",
    },
    divider: "#2D3748",
    line: "#2E3A47",
    brand: clientPrimary?.main ?? "#E17000",
    brand2: clientSecondary.main,
    // Text/icon variants of brand/brand2 — lightened until readable on the
    // dark paper (unchanged when the tenant colour already reads fine).
    brandText: readableOn(clientPrimary?.main ?? "#E17000", "#24303f"),
    brand2Text: readableOn(clientSecondary.main, "#24303f"),
    grayscale: common.white,
    error,
    info,
    mode: "dark",
    neutral,
    primary: clientPrimary ?? getPrimary(colorPreset),
    secondary: clientSecondary,
    success,
    text: {
      primary: "#aeb7c0",
      secondary: "#A0AEC0",
      disabled: "rgba(255, 255, 255, 0.48)",
    },
    warning,
  };
};
